import {
  ApplicantCompany,
  ApplicantEntrepreneurship,
  ApplicantReferee,
  ApplicationSection,
  User,
} from "@prisma/client";
import maxBy from "lodash.maxby";
import isNumber from "lodash/isNumber";

import { getApplicationSection } from "@/lib/application";
import {
  MondayPlatformStatus,
  prepareMondayPlatformStatus,
} from "@/lib/integration/monday/lib/status";
import { AppPages, getApplicationRelLink } from "@/lib/pages";

import { saveApplicationSection } from "@/core/client/application/applicationSection";
import { updateMondayFetItemPlatformStatus } from "@/core/client/monday/platformStatus";

import { ApplicantPlan } from "./application";
import { isAdmin } from "./auth/rbac";
import { ApplicationSectionName } from "./experienceType";
import { ApplicationFull } from "./prisma";

export enum ApplicationSectionStatusType {
  Created = 1,
  SubmittedForReview = 2,
  ReviewCompleted = 3,
  Boosting = 4,
}

export enum ApplicationSectionStatusTitle {
  Created = "Created",
  SubmittedForReview = "Submitted for review",
  ReviewCompleted = "Review completed",
  Boosting = "Boosting",
}

export enum ApplicationSectionColumn {
  Employment = "companyIdStatusId",
  Entrepreneurship = "entrepreneurshipIdStatusId",
  ProfJourneyExtra = "profJourneyExtraStatusId",
  ProfJourney = "profJourneyStatusId",
  ExternalProfile = "externalProfileStatusId",
  PotentialReferees = "potentialRefereesStatusId",
  RefereesChoice = "refereesChoiceStatusId",
  RefereeDetails = "refereeIdStatusId",
  ExternalProfileStepDocStatusId = "externalProfileStepDocStatusId",
}

export const getApplicationSectionStatusTitle = (
  statusType: ApplicationSectionStatusType
): ApplicationSectionStatusTitle => {
  switch (statusType) {
    case ApplicationSectionStatusType.Created:
    default:
      return ApplicationSectionStatusTitle.Created;
    case ApplicationSectionStatusType.SubmittedForReview:
      return ApplicationSectionStatusTitle.SubmittedForReview;
    case ApplicationSectionStatusType.ReviewCompleted:
      return ApplicationSectionStatusTitle.ReviewCompleted;
    case ApplicationSectionStatusType.Boosting:
      return ApplicationSectionStatusTitle.Boosting;
  }
};

// For static section statuses (Prof Journey Extra, External Profile, etc)
export const getApplicationSectionStatus = (
  application: ApplicationFull,
  applicationSection: ApplicationSectionColumn
): ApplicationSectionStatusType | null => {
  const section = getApplicationSection(application);
  if (section) {
    const sectionStatus = section[applicationSection];
    switch (sectionStatus) {
      case ApplicationSectionStatusType.Created:
        return ApplicationSectionStatusType.Created;
      case ApplicationSectionStatusType.SubmittedForReview:
        return ApplicationSectionStatusType.SubmittedForReview;
      case ApplicationSectionStatusType.ReviewCompleted:
        return ApplicationSectionStatusType.ReviewCompleted;
      case ApplicationSectionStatusType.Boosting:
        return ApplicationSectionStatusType.Boosting;
    }
  }

  return null;
};

export const getApplicationRefereesDetailsSectionStatusList = (
  application: ApplicationFull
): Array<any> | null => {
  const section = getApplicationSection(application);
  if (section) {
    const sectionStatus = section[ApplicationSectionColumn.RefereeDetails];
    if (Array.isArray(sectionStatus)) {
      return section[ApplicationSectionColumn.RefereeDetails];
    }
  }

  return null;
};

// For dynamic section statuses (Employment, Referees, etc)
// For dynamic section statuses (Employment, Referees, etc)
export const getApplicationSectionStatusFromArrayItem = (
  application: ApplicationFull,
  applicationSection: ApplicationSectionColumn,
  applicationSectionStatusItemId: string
): ApplicationSectionStatusType | null => {
  const section = getApplicationSection(application);
  if (section) {
    const sectionStatus = section[applicationSection];
    if (Array.isArray(sectionStatus)) {
      const sectionStatusItem = sectionStatus.find(
        (x: any) => x.id === applicationSectionStatusItemId
      );
      switch ((sectionStatusItem as any)?.statusId) {
        case ApplicationSectionStatusType.SubmittedForReview:
          return ApplicationSectionStatusType.SubmittedForReview;
        case ApplicationSectionStatusType.ReviewCompleted:
          return ApplicationSectionStatusType.ReviewCompleted;
      }
    }
  }

  return null;
};

export const isApplicantRefereeDetailsSubmitted = (
  application: ApplicationFull,
  refereeId: string
): boolean => {
  return (
    getApplicationSectionStatusFromArrayItem(
      application,
      ApplicationSectionColumn.RefereeDetails,
      refereeId
    ) !== null
  );
};

export const getApplicationEmploymentsAndEntrepreneurshipsSectionStatus = (
  application: ApplicationFull,
  companyList: ApplicantCompany[],
  entrepreneurshipList: ApplicantEntrepreneurship[]
): ApplicationSectionStatusType | null => {
  const section = getApplicationSection(application);
  const existsIds = [
    ...companyList.map((item) => item.id),
    ...entrepreneurshipList.map((item) => item.id),
  ];
  if (section) {
    let resultStatusList: any[] = section[
      ApplicationSectionColumn.Employment
    ].concat(section[ApplicationSectionColumn.Entrepreneurship]);

    resultStatusList = resultStatusList.filter((item) =>
      existsIds.includes(item.id)
    );

    if (Array.isArray(resultStatusList) && resultStatusList.length > 0) {
      if (
        resultStatusList.some(
          (x: any) => x.statusId === ApplicationSectionStatusType.Created
        )
      ) {
        return ApplicationSectionStatusType.Created;
      }
      if (
        resultStatusList.some(
          (x: any) =>
            x.statusId === ApplicationSectionStatusType.SubmittedForReview
        )
      ) {
        return ApplicationSectionStatusType.SubmittedForReview;
      }
      if (
        resultStatusList.some(
          (x: any) =>
            x.statusId === ApplicationSectionStatusType.SubmittedForReview
        )
      ) {
        return ApplicationSectionStatusType.SubmittedForReview;
      }
      if (
        resultStatusList.every(
          (x: any) =>
            x.statusId === ApplicationSectionStatusType.ReviewCompleted
        )
      ) {
        return ApplicationSectionStatusType.ReviewCompleted;
      }
    }
  }
  return null;
};

// TODO: Make status update / item remove in Monday
export const deleteApplicationSectionStatus = (
  application: ApplicationFull,
  applicationSectionColumn: ApplicationSectionColumn,
  applicationSectionStatusItemId?: string // For dynamic array of id records (employments, referees, etc),
) => {
  const section = getApplicationSection(application);
  if (!section) {
    throw new Error("Can't find status section for application");
  }

  // For dynamic section statuses
  if (
    section &&
    Array.isArray(section[applicationSectionColumn]) &&
    applicationSectionStatusItemId
  ) {
    const currentApplicationSectionStatus =
      getApplicationSectionStatusFromArrayItem(
        application,
        applicationSectionColumn,
        applicationSectionStatusItemId
      );

    // If application do not have any statuses
    if (currentApplicationSectionStatus === null) {
      return;
    } else {
      section[applicationSectionColumn] = (
        section[applicationSectionColumn] as []
      ).filter((x: any) => x.id !== applicationSectionStatusItemId) as any;
    }
  } else {
    // For static section statuses, simply assign new value
    (section[applicationSectionColumn] as any) = null;
  }

  saveApplicationSection(application.id, section, true);
};

// TODO: Handle optimistic UI, with showing statuses before server responses
export const saveApplicationSectionWithMondaySync = async ({
  application,
  ownUser,
  applicationSectionName,
  applicationSectionColumn,
  currentApplicationSectionStatus,
  mondayItemNameToSave,
  applicationSectionStatusItemId, // For dynamic array of id records (employments, referees, etc),
  toSetApplicationSectionStatus,
  sendFeedback,
  isSelfService,
}: {
  application: ApplicationFull;
  ownUser: User;
  applicationSectionName: ApplicationSectionName;
  applicationSectionColumn: ApplicationSectionColumn;
  currentApplicationSectionStatus: ApplicationSectionStatusType | null;
  mondayItemNameToSave: string;
  applicationSectionStatusItemId?: string;
  toSetApplicationSectionStatus?: ApplicationSectionStatusType | null;
  sendFeedback?: boolean;
  isSelfService?: boolean;
}) => {
  let statusToMonday = "" as MondayPlatformStatus;
  let additionalData = undefined as Record<string, any> | undefined;

  if (isSelfService) {
    statusToMonday = MondayPlatformStatus.COMPLETE_EXPERIENCE;
  } else {
    switch (applicationSectionName) {
      case ApplicationSectionName.RefereeDetails: {
        if (sendFeedback && isAdmin(ownUser)) {
          statusToMonday = MondayPlatformStatus.SEND_FEEDBACK;
        }
        break;
      }
      case ApplicationSectionName.Employment:
      case ApplicationSectionName.Entrepreneurship:
      case ApplicationSectionName.ExternalProfile: {
        switch (currentApplicationSectionStatus) {
          case ApplicationSectionStatusType.Created:
          default: {
            statusToMonday = MondayPlatformStatus.REVIEW_EXPERIENCE;
            additionalData = {
              externalProfileUrl: getApplicationRelLink({
                applicationId: application.id,
                link: AppPages.ApplicationProfileExternalProfileIndex,
              }),
              profileUrl: getApplicationRelLink({
                applicationId: application.id,
                link: AppPages.ApplicationProfileIndex,
              }),
            };
            break;
          }
          case ApplicationSectionStatusType.SubmittedForReview: {
            if (sendFeedback && isAdmin(ownUser)) {
              statusToMonday = MondayPlatformStatus.SEND_FEEDBACK;
            } else if (isAdmin(ownUser)) {
              statusToMonday = MondayPlatformStatus.COMPLETE_EXPERIENCE;
            } else {
              statusToMonday = MondayPlatformStatus.REVIEW_EXPERIENCE;
            }
            additionalData = {
              profileUrl: getApplicationRelLink({
                applicationId: application.id,
                link: AppPages.ApplicationProfileIndex,
              }),
            };
            break;
          }
          case ApplicationSectionStatusType.Boosting: {
            if (sendFeedback && isAdmin(ownUser)) {
              statusToMonday = MondayPlatformStatus.SEND_FEEDBACK;
            } else if (isAdmin(ownUser)) {
              statusToMonday = MondayPlatformStatus.COMPLETE_EXPERIENCE;
            } else {
              statusToMonday = MondayPlatformStatus.REVIEW_EXPERIENCE;
            }
            break;
          }
        }
        break;
      }
      case ApplicationSectionName.PotentialReferees: {
        if (sendFeedback && isAdmin(ownUser)) {
          statusToMonday = MondayPlatformStatus.SEND_REFS_FEEDBACK;
        } else {
          statusToMonday = MondayPlatformStatus.REVIEW_POTENTIAL_REFS;
          additionalData = {
            externalProfileUrl: getApplicationRelLink({
              applicationId: application.id,
              link: AppPages.ApplicationProfileRefereesPotential,
            }),
          };
        }
        break;
      }
      case ApplicationSectionName.RefereesChoice: {
        switch (currentApplicationSectionStatus) {
          case null:
          case ApplicationSectionStatusType.Created: {
            if (
              application.applicant.applicantPlanId ===
              ApplicantPlan.FullSupport
            ) {
              statusToMonday = MondayPlatformStatus.REVIEW_REFS_CHOICE;
              break;
            }
            break;
          }

          case ApplicationSectionStatusType.SubmittedForReview: {
            if (isAdmin(ownUser)) {
              statusToMonday = MondayPlatformStatus.COMPLETE_REFS_CHOICE;
            }
            additionalData = {
              refereeList: getApplicationRelLink({
                applicationId: application.id,
                link: AppPages.ApplicationProfileIndex,
              }),
            };
            break;
          }
        }
        break;
      }
    }
  }

  const section = getApplicationSection(application);
  let newStatus = ApplicationSectionStatusType.Created;
  switch (currentApplicationSectionStatus) {
    case ApplicationSectionStatusType.Created:
    default: {
      updateMondayFetItemPlatformStatus(
        application.applicant.user.mondayClientItemId,
        prepareMondayPlatformStatus(mondayItemNameToSave, statusToMonday),
        additionalData
      );
      if (isSelfService) {
        newStatus = ApplicationSectionStatusType.ReviewCompleted;
      } else {
        newStatus = ApplicationSectionStatusType.SubmittedForReview;
      }
      break;
    }
    case ApplicationSectionStatusType.SubmittedForReview: {
      // GOTCHA: Skip updating Monday when Referees Choice review requested by QA (for full support)
      if (applicationSectionName === ApplicationSectionName.RefereesChoice) {
        if (
          application.applicant.applicantPlanId !== ApplicantPlan.LimitedSupport
        ) {
          if (isAdmin(ownUser)) {
            newStatus = ApplicationSectionStatusType.ReviewCompleted;
          }
        }

        updateMondayFetItemPlatformStatus(
          application.applicant.user.mondayClientItemId,
          prepareMondayPlatformStatus(mondayItemNameToSave, statusToMonday),
          additionalData
        );
      } else {
        if (isAdmin(ownUser) && sendFeedback) {
          newStatus = ApplicationSectionStatusType.Created;
        } else if (isAdmin(ownUser)) {
          newStatus = ApplicationSectionStatusType.ReviewCompleted;
        } else {
          newStatus = ApplicationSectionStatusType.SubmittedForReview;
          mondayItemNameToSave = `${mondayItemNameToSave} (Repeat ${new Date().toLocaleString(
            "en-US"
          )})`;
        }
        updateMondayFetItemPlatformStatus(
          application.applicant.user.mondayClientItemId,
          prepareMondayPlatformStatus(mondayItemNameToSave, statusToMonday),
          additionalData
        );
      }
      break;
    }
    case ApplicationSectionStatusType.ReviewCompleted: {
      // GOTCHA: Nothing changes right now, to be changed later
      newStatus = ApplicationSectionStatusType.ReviewCompleted;
      if (isAdmin(ownUser) && sendFeedback) {
        newStatus = ApplicationSectionStatusType.Created;
        updateMondayFetItemPlatformStatus(
          application.applicant.user.mondayClientItemId,
          prepareMondayPlatformStatus(mondayItemNameToSave, statusToMonday),
          additionalData
        );
      }
      break;
    }
    case ApplicationSectionStatusType.Boosting: {
      newStatus = ApplicationSectionStatusType.ReviewCompleted;
      updateMondayFetItemPlatformStatus(
        application.applicant.user.mondayClientItemId,
        prepareMondayPlatformStatus(mondayItemNameToSave, statusToMonday),
        additionalData
      );
      break;
    }
  }

  if (
    currentApplicationSectionStatus === null &&
    toSetApplicationSectionStatus !== null &&
    toSetApplicationSectionStatus !== undefined
  ) {
    newStatus = toSetApplicationSectionStatus;
  }

  const resultSection = {
    id: section.id,
    applicationId: application.id,
  } as ApplicationSection;
  // For dynamic section statuses
  if (
    (applicationSectionColumn === "companyIdStatusId" ||
      applicationSectionColumn === "entrepreneurshipIdStatusId" ||
      applicationSectionColumn === "refereeIdStatusId") &&
    applicationSectionStatusItemId
  ) {
    resultSection[applicationSectionColumn] = {
      id: applicationSectionStatusItemId,
      statusId: newStatus,
    } as any;
  } else {
    // For static section statuses, simply assign new value
    resultSection[applicationSectionColumn] = newStatus as any;

    if (newStatus === ApplicationSectionStatusType.SubmittedForReview) {
      if (applicationSectionName === ApplicationSectionName.ExternalProfile) {
        resultSection.isProfJourneyUnlocked = true;
        resultSection.externalProfileReviewIteration = isNumber(
          section.externalProfileReviewIteration
        )
          ? section.externalProfileReviewIteration + 1
          : null;
      }
      if (applicationSectionName === ApplicationSectionName.PotentialReferees) {
        resultSection.potentialRefereesReviewIteration = isNumber(
          section.potentialRefereesReviewIteration
        )
          ? section.potentialRefereesReviewIteration + 1
          : section.potentialRefereesReviewIteration;
      }
      if (applicationSectionName === ApplicationSectionName.RefereesChoice) {
        resultSection.refereesChoiceReviewIteration = isNumber(
          section.refereesChoiceReviewIteration
        )
          ? section.refereesChoiceReviewIteration + 1
          : section.refereesChoiceReviewIteration;
      }
    }

    if (newStatus === ApplicationSectionStatusType.ReviewCompleted) {
      if (applicationSectionName === ApplicationSectionName.ExternalProfile) {
        resultSection.isExternalProfileValid = true;
        if (!getApplicationSection(application)?.hasExternalProfileBoosted) {
          // If External Profile finished without Boosting it's non-boosted application
          resultSection.hasExternalProfileBoosted = false;
        }
      }
      if (applicationSectionName === ApplicationSectionName.ProfJourney) {
        resultSection.isProfJourneyValid = true;
      }
      if (applicationSectionName === ApplicationSectionName.RefereeDetails) {
        resultSection.isRefereesValid = true;
      }
    }
  }

  await saveApplicationSection(application.id, resultSection);

  return newStatus;
};

export const checkActionButtonVisibleDueToStatus = (
  ownUser: User,
  application: ApplicationFull,
  applicationSectionStatus: ApplicationSectionStatusType | null
) => {
  const isUserApplication = ownUser.id === application.applicant?.user.id;

  return (
    applicationSectionStatus === ApplicationSectionStatusType.ReviewCompleted ||
    !(
      ownUser.roleId === 1 &&
      isUserApplication &&
      applicationSectionStatus ===
        ApplicationSectionStatusType.SubmittedForReview
    )
  );
};

export const compareSectionAndLatestRefereeCreatedTime = (
  applicationSection: ApplicationSection,
  referees: ApplicantReferee[]
): boolean => {
  const refereeTimeList = referees.map((x) => new Date(x.created).getTime());
  const latestReferee = maxBy(refereeTimeList);
  if (latestReferee === undefined) return false;

  const result = new Date(applicationSection.edited).getTime() < latestReferee;
  return result;
};
